// src/Views/BuildsPage.js — Flasher BuildsSection.
// Status pills, accent rails, hover lift, recognition panel.
import React, { useState } from 'react';
import { Eyebrow, ScrambleText } from '../components/primitives';
import { BUILD_SECTIONS, RECOGNITIONS } from './siteData';

const STATUS_TINT = {
  Shipped:        { fg: 'var(--cyan)',    bg: 'rgba(0,223,187,0.12)',   glow: 'rgba(0,223,187,0.4)'  },
  Active:         { fg: 'var(--magenta)', bg: 'rgba(255,177,63,0.12)',  glow: 'rgba(255,177,63,0.4)' },
  'In Progress':  { fg: 'var(--purple)',  bg: 'rgba(160,102,255,0.12)', glow: 'rgba(160,102,255,0.4)' },
};

const ACCENT_VAR = {
  magenta: 'var(--magenta)',
  cyan: 'var(--cyan)',
  purple: 'var(--purple)',
};

function SectionHeader({ label, sublabel, index, total }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        paddingBottom: 16,
        borderBottom: '1px solid var(--line)',
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--f-mono)',
          fontSize: 11,
          letterSpacing: '0.2em',
          color: 'var(--ink-3)',
        }}
      >
        {String(index).padStart(2, '0')} / {String(total).padStart(2, '0')}
      </span>
      <span
        style={{
          fontFamily: 'var(--f-mono)',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.18em',
          padding: '4px 10px',
          borderRadius: 999,
          background: 'rgba(160,102,255,0.12)',
          color: 'var(--purple)',
          border: '1px solid var(--line-strong)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: 'var(--f-display)',
          fontSize: 24,
          fontWeight: 500,
          color: 'var(--ink)',
          flex: 1,
          minWidth: 220,
        }}
      >
        {sublabel}
      </span>
    </div>
  );
}

function BuildCard({ title, status, domain, description, link, accent = 'purple', index = 0 }) {
  const [hover, setHover] = useState(false);
  const tint = STATUS_TINT[status] || STATUS_TINT['In Progress'];
  const accentColor = ACCENT_VAR[accent] || 'var(--purple)';
  const isExternal = link && /^https?:/i.test(link);
  const Tag = link ? 'a' : 'div';

  return (
    <Tag
      href={link}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 24,
        borderRadius: 18,
        background: hover
          ? 'linear-gradient(160deg, rgba(160,102,255,0.10), rgba(255,177,63,0.06))'
          : 'rgba(15, 13, 42, 0.55)',
        border: '1px solid var(--line)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        cursor: link ? 'pointer' : 'default',
        textDecoration: 'none',
        color: 'var(--ink)',
        overflow: 'hidden',
        transform: hover ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hover
          ? `0 0 0 1px ${accentColor}55, 0 24px 60px -20px ${accentColor}, 0 0 80px ${accentColor}22`
          : '0 4px 16px rgba(0,0,0,0.3)',
        transition: 'transform 0.3s cubic-bezier(.2,1,.2,1), box-shadow 0.3s, background 0.3s',
        minHeight: 220,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: hover ? '100%' : '40%',
          height: 2,
          background: accentColor,
          boxShadow: `0 0 14px ${accentColor}`,
          transition: 'width 0.4s cubic-bezier(.2,1,.2,1)',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 16,
          right: 20,
          fontFamily: 'var(--f-mono)',
          fontSize: 11,
          color: 'var(--ink-3)',
          letterSpacing: '0.16em',
        }}
      >
        ◐ {String(index + 1).padStart(2, '0')}
      </div>

      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          alignSelf: 'flex-start',
          fontFamily: 'var(--f-mono)',
          fontSize: 10,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: tint.fg,
          padding: '4px 10px',
          borderRadius: 999,
          background: tint.bg,
          border: `1px solid ${tint.fg}33`,
          marginTop: 4,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: tint.fg,
            boxShadow: `0 0 8px ${tint.glow}`,
          }}
        />
        {status}
      </div>

      <h3
        style={{
          fontFamily: 'var(--f-display)',
          fontSize: 26,
          fontWeight: 600,
          lineHeight: 1.05,
          letterSpacing: '-0.02em',
          margin: '4px 0 0',
          color: 'var(--ink)',
        }}
      >
        {title}
      </h3>

      <div
        style={{
          fontFamily: 'var(--f-mono)',
          fontSize: 11,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
        }}
      >
        {domain}
      </div>

      <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)', margin: 0, flex: 1 }}>
        {description}
      </p>

      {link && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: 'var(--f-mono)',
            fontSize: 12,
            letterSpacing: '0.12em',
            color: accentColor,
            marginTop: 4,
          }}
        >
          {isExternal ? 'OPEN.LIVE' : 'VIEW.CASE'}
          <span
            style={{
              display: 'inline-block',
              transform: hover ? 'translateX(6px)' : 'translateX(0)',
              transition: 'transform 0.3s',
            }}
          >
            ↗
          </span>
        </div>
      )}
    </Tag>
  );
}

function RecognitionPanel() {
  return (
    <div
      style={{
        marginTop: 40,
        padding: 32,
        borderRadius: 24,
        background: 'linear-gradient(135deg, rgba(160,102,255,0.06), rgba(255,177,63,0.04))',
        border: '1px solid var(--line-strong)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,177,63,0.18), transparent 70%)',
          filter: 'blur(20px)',
        }}
      />
      <div
        className="recog-grid"
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
          gap: 32,
          alignItems: 'center',
        }}
      >
        <div>
          <Eyebrow accent="var(--cyan)">RECOGNITION</Eyebrow>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {RECOGNITIONS.map((r) => (
            <div
              key={r.title + r.detail}
              style={{
                display: 'flex',
                gap: 14,
                alignItems: 'baseline',
                paddingBottom: 14,
                borderBottom: '1px dashed var(--line)',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: 11,
                  color: 'var(--magenta)',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  minWidth: 140,
                }}
              >
                {r.title}
              </span>
              <span style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.5 }}>{r.detail}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const BuildsPage = () => {
  return (
    <section id="builds" style={{ position: 'relative', padding: '120px 28px', zIndex: 2 }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          className="section-header-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 420px)',
            alignItems: 'end',
            gap: 32,
            marginBottom: 64,
          }}
        >
          <div>
            <Eyebrow accent="var(--magenta)">◇ BUILDS — Index 01—06</Eyebrow>
            <h2
              style={{
                fontFamily: 'var(--f-display)',
                fontSize: 'clamp(48px, 7vw, 112px)',
                fontWeight: 700,
                lineHeight: 0.94,
                letterSpacing: '-0.04em',
                margin: 0,
                color: 'var(--ink)',
                overflow: 'hidden',
              }}
            >
              <ScrambleText duration={1200} style={{ maxWidth: '100%' }}>Working systems.</ScrambleText>
              <br />
              <span
                style={{
                  fontStyle: 'italic',
                  fontWeight: 400,
                  background: 'linear-gradient(120deg, var(--magenta), var(--purple), var(--cyan))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  paddingRight: '0.15em',
                }}
              >
                Different worlds.
              </span>
            </h2>
          </div>
          <p style={{ maxWidth: 420, fontSize: 16, lineHeight: 1.55, color: 'var(--ink-2)', margin: 0 }}>
            Each one built from a real problem, designed as a system, and constructed
            with AI agents executing my specifications.
          </p>
        </div>

        {BUILD_SECTIONS.map((section, sIdx) => (
          <div key={section.label} style={{ marginBottom: 80 }}>
            <SectionHeader
              label={section.label}
              sublabel={section.sublabel}
              index={sIdx + 1}
              total={BUILD_SECTIONS.length}
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: 20,
                marginTop: 28,
              }}
            >
              {section.builds.map((b, i) => (
                <BuildCard key={b.title} {...b} index={i} />
              ))}
            </div>
          </div>
        ))}

        <RecognitionPanel />
      </div>
    </section>
  );
};

export default BuildsPage;
